import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { platformApiFetch } from './platformApi';

function statusColor(s) {
  if (s === 'active') return 'success';
  return 'default';
}

export default function PlatformApiKeyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/api-keys/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setApiKey(json.apiKey);
    } catch (e) {
      setError(e.message);
      setApiKey(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !apiKey) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!apiKey) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{apiKey.key_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Tenant: {apiKey.tenant_name || '—'} ({apiKey.tenant_code || apiKey.tenant_id})
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
            {apiKey.masked_key}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/platform/api-keys')}>
          Back to list
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {row('ID', apiKey.id)}
          {row('Tenant ID', apiKey.tenant_id)}
          {row('Key prefix', apiKey.key_prefix)}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip size="small" label={apiKey.status} color={statusColor(apiKey.status)} />
            </Box>
          </Grid>
          {row(
            'Scopes',
            Array.isArray(apiKey.scopes) && apiKey.scopes.length ? apiKey.scopes.join(', ') : '—'
          )}
          {row('Expires', apiKey.expires_at ? new Date(apiKey.expires_at).toLocaleString() : 'Never')}
          {row('Last used', apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString() : '—')}
          {row('Revoked', apiKey.revoked_at ? new Date(apiKey.revoked_at).toLocaleString() : '—')}
          {row('Creator', apiKey.creator_email || (apiKey.created_by_tenant_user_id ? `#${apiKey.created_by_tenant_user_id}` : '—'))}
          {row('Rotated from ID', apiKey.rotated_from_id)}
          {row('Created', apiKey.created_at ? new Date(apiKey.created_at).toLocaleString() : '—')}
          {row('Updated', apiKey.updated_at ? new Date(apiKey.updated_at).toLocaleString() : '—')}
        </Grid>
      </Paper>
    </Box>
  );
}
